// Address validation utilities

export interface AddressValidationResult {
  isValid: boolean;
  errors: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  warnings?: string[];
}

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

// US state abbreviations
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC' // Washington DC
];

// Canadian province abbreviations
const CA_PROVINCES = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'
];

// Postal code patterns by country
const POSTAL_PATTERNS: Record<string, RegExp> = {
  US: /^\d{5}(-\d{4})?$/, // 12345 or 12345-6789
  CA: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i, // A1B 2C3 or A1B2C3
  GB: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i, // SW1A 1AA
  AU: /^\d{4}$/, // 2000
  DE: /^\d{5}$/, // 10115
  FR: /^\d{5}$/, // 75001
  IT: /^\d{5}$/, // 00100
  ES: /^\d{5}$/, // 28001
  NL: /^\d{4}\s?[A-Z]{2}$/i, // 1234 AB
  BE: /^\d{4}$/, // 1000
  CH: /^\d{4}$/, // 8000
  AT: /^\d{4}$/, // 1010
  JP: /^\d{3}-?\d{4}$/, // 100-0001
  BR: /^\d{5}-?\d{3}$/, // 01310-100
  MX: /^\d{5}$/, // 01000
  DEFAULT: /.+/ // Any non-empty string for other countries
};

// Common address line patterns that might indicate PO Boxes
const PO_BOX_PATTERNS = [
  /^p\.?\s*o\.?\s*box/i,
  /^post\s*office\s*box/i,
  /^po\s*box/i,
  /^box\s*\d+/i
];

export function validateAddress(address: Address): AddressValidationResult {
  const errors: AddressValidationResult['errors'] = {};
  const warnings: string[] = [];

  // Validate line1 (required)
  if (!address.line1 || address.line1.trim().length === 0) {
    errors.line1 = 'Street address is required';
  } else if (address.line1.trim().length < 3) {
    errors.line1 = 'Street address is too short';
  } else if (address.line1.trim().length > 100) {
    errors.line1 = 'Street address is too long';
  } else {
    // Check for PO Box
    const isPOBox = PO_BOX_PATTERNS.some(pattern => pattern.test(address.line1));
    if (isPOBox) {
      warnings.push('PO Box addresses may not be accepted for all services');
    }
  }

  // Validate line2 (optional)
  if (address.line2 && address.line2.trim().length > 100) {
    errors.line2 = 'Address line 2 is too long';
  }

  // Validate city (required)
  if (!address.city || address.city.trim().length === 0) {
    errors.city = 'City is required';
  } else if (address.city.trim().length < 2) {
    errors.city = 'City name is too short';
  } else if (address.city.trim().length > 50) {
    errors.city = 'City name is too long';
  } else if (!/^[a-zA-Z\s\-'.]+$/.test(address.city.trim())) {
    errors.city = 'City contains invalid characters';
  }

  // Validate state/province based on country
  if (!address.state || address.state.trim().length === 0) {
    errors.state = 'State/Province is required';
  } else {
    const stateUpper = address.state.trim().toUpperCase();
    
    if (address.country === 'US') {
      if (!US_STATES.includes(stateUpper)) {
        errors.state = 'Invalid US state abbreviation';
      }
    } else if (address.country === 'CA') {
      if (!CA_PROVINCES.includes(stateUpper)) {
        errors.state = 'Invalid Canadian province abbreviation';
      }
    }
    // For other countries, just ensure it's not too long
    else if (address.state.trim().length > 50) {
      errors.state = 'State/Province is too long';
    }
  }

  // Validate postal code based on country
  if (!address.postal_code || address.postal_code.trim().length === 0) {
    errors.postal_code = 'Postal/ZIP code is required';
  } else {
    const pattern = POSTAL_PATTERNS[address.country] || POSTAL_PATTERNS.DEFAULT;
    if (!pattern.test(address.postal_code.trim())) {
      errors.postal_code = `Invalid ${address.country === 'US' ? 'ZIP' : 'postal'} code format`;
    }
  }

  // Validate country (required)
  if (!address.country || address.country.trim().length === 0) {
    errors.country = 'Country is required';
  } else if (address.country.length !== 2) {
    errors.country = 'Country must be a 2-letter code';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings: warnings.length > 0 ? warnings : undefined
  };
}

// Format address for display
export function formatAddress(address: Address): string {
  const parts = [
    address.line1,
    address.line2,
    `${address.city}, ${address.state} ${address.postal_code}`,
    address.country
  ].filter(Boolean);
  
  return parts.join('\n');
}

// Normalize address input (trim whitespace, uppercase state/country)
export function normalizeAddress(address: Address): Address {
  const normalized: Address = {
    line1: address.line1.trim(),
    city: address.city.trim(),
    state: address.state.trim().toUpperCase(),
    postal_code: address.postal_code.trim().toUpperCase(),
    country: address.country.trim().toUpperCase()
  };
  
  // Only include line2 if it has a value
  if (address.line2?.trim()) {
    normalized.line2 = address.line2.trim();
  }
  
  return normalized;
}

// Get list of countries we support
export const SUPPORTED_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'JP', name: 'Japan' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' }
];

// Get states/provinces for a country
export function getStatesForCountry(countryCode: string): Array<{code: string, name: string}> | null {
  if (countryCode === 'US') {
    return [
      { code: 'AL', name: 'Alabama' },
      { code: 'AK', name: 'Alaska' },
      { code: 'AZ', name: 'Arizona' },
      { code: 'AR', name: 'Arkansas' },
      { code: 'CA', name: 'California' },
      { code: 'CO', name: 'Colorado' },
      { code: 'CT', name: 'Connecticut' },
      { code: 'DE', name: 'Delaware' },
      { code: 'FL', name: 'Florida' },
      { code: 'GA', name: 'Georgia' },
      { code: 'HI', name: 'Hawaii' },
      { code: 'ID', name: 'Idaho' },
      { code: 'IL', name: 'Illinois' },
      { code: 'IN', name: 'Indiana' },
      { code: 'IA', name: 'Iowa' },
      { code: 'KS', name: 'Kansas' },
      { code: 'KY', name: 'Kentucky' },
      { code: 'LA', name: 'Louisiana' },
      { code: 'ME', name: 'Maine' },
      { code: 'MD', name: 'Maryland' },
      { code: 'MA', name: 'Massachusetts' },
      { code: 'MI', name: 'Michigan' },
      { code: 'MN', name: 'Minnesota' },
      { code: 'MS', name: 'Mississippi' },
      { code: 'MO', name: 'Missouri' },
      { code: 'MT', name: 'Montana' },
      { code: 'NE', name: 'Nebraska' },
      { code: 'NV', name: 'Nevada' },
      { code: 'NH', name: 'New Hampshire' },
      { code: 'NJ', name: 'New Jersey' },
      { code: 'NM', name: 'New Mexico' },
      { code: 'NY', name: 'New York' },
      { code: 'NC', name: 'North Carolina' },
      { code: 'ND', name: 'North Dakota' },
      { code: 'OH', name: 'Ohio' },
      { code: 'OK', name: 'Oklahoma' },
      { code: 'OR', name: 'Oregon' },
      { code: 'PA', name: 'Pennsylvania' },
      { code: 'RI', name: 'Rhode Island' },
      { code: 'SC', name: 'South Carolina' },
      { code: 'SD', name: 'South Dakota' },
      { code: 'TN', name: 'Tennessee' },
      { code: 'TX', name: 'Texas' },
      { code: 'UT', name: 'Utah' },
      { code: 'VT', name: 'Vermont' },
      { code: 'VA', name: 'Virginia' },
      { code: 'WA', name: 'Washington' },
      { code: 'WV', name: 'West Virginia' },
      { code: 'WI', name: 'Wisconsin' },
      { code: 'WY', name: 'Wyoming' },
      { code: 'DC', name: 'District of Columbia' }
    ];
  } else if (countryCode === 'CA') {
    return [
      { code: 'AB', name: 'Alberta' },
      { code: 'BC', name: 'British Columbia' },
      { code: 'MB', name: 'Manitoba' },
      { code: 'NB', name: 'New Brunswick' },
      { code: 'NL', name: 'Newfoundland and Labrador' },
      { code: 'NS', name: 'Nova Scotia' },
      { code: 'NT', name: 'Northwest Territories' },
      { code: 'NU', name: 'Nunavut' },
      { code: 'ON', name: 'Ontario' },
      { code: 'PE', name: 'Prince Edward Island' },
      { code: 'QC', name: 'Quebec' },
      { code: 'SK', name: 'Saskatchewan' },
      { code: 'YT', name: 'Yukon' }
    ];
  }
  
  return null;
}